<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
         @click.self="dismiss" @keydown="handleKeydown">
      <div ref="dialog" role="dialog" aria-modal="true" aria-labelledby="upgrade-dialog-title" tabindex="-1"
           class="upgrade-dialog relative flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-700/60 bg-gray-900 text-gray-100 shadow-2xl"
           style="max-height: min(88dvh, calc(100dvh - 32px));">
        <div class="flex items-start justify-between gap-3 px-5 pt-5 sm:px-6">
          <div class="min-w-0">
            <h2 id="upgrade-dialog-title" class="text-lg font-semibold leading-snug">{{ title }}</h2>
            <p class="mt-1.5 text-sm leading-relaxed text-gray-400">{{ description }}</p>
          </div>
          <button ref="closeButton" type="button" class="musicmint-dismiss shrink-0" :aria-label="t('common.close')" @click="dismiss">
            <span class="i-lucide-x h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div class="min-h-0 overflow-y-auto overscroll-contain px-5 pb-5 pt-4 sm:px-6">
          <p v-if="isAuthenticated" class="mb-4 rounded-lg bg-gray-800/70 px-3 py-2 text-xs text-gray-300">
            {{ request?.requiredPoints ? t('paywall.balanceNeeded', { balance: totalPoints, required: request.requiredPoints }) : t('paywall.balance', { balance: totalPoints }) }}
          </p>

          <div v-if="loading" class="space-y-3" role="status" :aria-label="t('common.loading')">
            <div v-for="index in 2" :key="index" class="h-[92px] animate-pulse rounded-xl bg-gray-800/70" />
          </div>
          <div v-else-if="loadError" class="rounded-xl border border-gray-700 p-4 text-center text-sm text-gray-300">
            <p role="alert">{{ t('billing.plansUnavailable') }}</p>
            <button type="button" class="musicmint-secondary-button mt-3 min-h-[40px] px-4" @click="load">{{ t('billing.checkAgain') }}</button>
          </div>
          <template v-else>
            <p v-if="hasSubscription && !offers.length" class="rounded-xl border border-gray-700 p-4 text-sm leading-relaxed text-gray-300">{{ t('paywall.memberNoUpgrade') }}</p>
            <ul v-else class="space-y-3">
              <li v-for="plan in offers" :key="plan.product_id">
                <button type="button" class="plan-option" :class="{ recommended: plan.product_id === recommendedId }"
                        :disabled="!!choosing || checkoutUnavailable" @click="choose(plan)">
                  <span class="flex min-w-0 flex-1 flex-col items-start text-left">
                    <span class="flex flex-wrap items-center gap-2">
                      <strong class="text-sm font-semibold text-white">{{ plan.name }}</strong>
                      <span v-if="plan.product_id === recommendedId && !hasSubscription" class="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{{ t('paywall.recommended') }}</span>
                    </span>
                    <span class="mt-1 text-xs text-gray-400">{{ songsFor(plan.points) > 0 ? t('paywall.allowance', { credits: creditsLabel(plan), songs: songsFor(plan.points).toLocaleString(locale) }) : creditsLabel(plan) }}</span>
                    <span v-if="offer(plan).hasIntro" class="mt-0.5 text-[11px] text-gray-500">{{ t('billing.renewsMonthly', { price: formatPrice(offer(plan).regular) }) }}</span>
                    <span v-else-if="plan.tag === 'year'" class="mt-0.5 text-[11px] text-gray-500">{{ t('billing.annualTotal', { price: formatPrice(offer(plan).regular) }) }}</span>
                  </span>
                  <span class="flex shrink-0 flex-col items-end">
                    <span v-if="offer(plan).hasIntro" class="text-xs text-gray-500 line-through">{{ formatPrice(offer(plan).regular) }}</span>
                    <strong class="text-lg font-semibold tabular-nums text-white">{{ formatPrice(offer(plan).display) }}</strong>
                    <span class="text-[11px] text-gray-400">/ {{ t(plan.tag === 'week' ? 'billing.unit_week' : 'billing.unit_month') }}</span>
                  </span>
                  <span class="plan-cta">{{ choosing === plan.product_id ? t('common.loading') : hasSubscription ? t('billing.upgradePlan') : t('paywall.continue') }}</span>
                </button>
              </li>
            </ul>
          </template>

          <p v-if="error" class="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300" role="alert">{{ t(error) }}</p>
          <p class="mt-4 text-center text-[11px] text-gray-500">{{ t('billing.secureCheckout') }} · {{ t('billing.noCancellationFee') }}</p>
          <div class="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm">
            <NuxtLink :to="comparePath" class="inline-flex min-h-[40px] items-center text-blue-300 underline" @click="onCompare">{{ t('paywall.comparePlans') }}</NuxtLink>
            <NuxtLink v-if="hasSubscription" to="/billing" class="inline-flex min-h-[40px] items-center text-blue-300 underline" @click="closePaywall">{{ t('billing.accountPageTitle') }}</NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { billingService, type BillingConfig } from '~/services/billingService';
import { musicService } from '~/services/musicService';
import { useAuthState } from '~/composables/useAuthState';
import { usePaywall } from '~/composables/usePaywall';
import { useDialogFocus } from '~/composables/useDialogFocus';
import { trackEvent } from '~/utils/analytics';
import { planOffer, planAmount, isCurrentFastSpringMember } from '~/utils/subscription-offers.mjs';
import { isStripeHostedUrl, canUpgradePlan } from '~/utils/billing-route.mjs';
import { rememberCheckoutSource, songsForCredits, pricingPath } from '~/utils/checkout-source.mjs';

interface Plan { product_id: string; group_id: string; tag: string; name: string; price: string; price_desc?: string; points: number }

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const { request, closePaywall } = usePaywall();
const { isAuthenticated, totalPoints, userAccount, fetchAndStoreToken, handleSignIn } = useAuthState();

const visible = computed(() => !!request.value);
const plans = ref<Plan[]>([]);
const musicCost = ref(0);
const config = ref<BillingConfig | null>(null);
const loading = ref(false);
const loadError = ref(false);
const choosing = ref('');
const error = ref('');
let loadRequest = 0;

const source = computed(() => request.value ? `paywall_${request.value.surface}_${request.value.trigger}` : 'paywall');
const comparePath = computed(() => pricingPath(source.value));
const hasSubscription = computed(() => config.value?.has_subscription === true);
const checkoutUnavailable = computed(() => !hasSubscription.value && config.value?.checkout_available === false);
const introEligible = computed(() => config.value?.intro_eligible === true);
const monthlyPlans = computed(() => plans.value
  .filter(plan => plan.tag === 'month' && plan.group_id !== 'free' && planAmount(plan.price) > 0)
  .sort((a, b) => planAmount(a.price) - planAmount(b.price)));
// Members can only move to a plan Stripe allows as an upgrade; everyone else sees the monthly plans.
const offers = computed(() => {
  if (!hasSubscription.value) return monthlyPlans.value;
  if (isCurrentFastSpringMember(config.value)) return [];
  return plans.value.filter(plan => canUpgradePlan(config.value, plan.product_id))
    .sort((a, b) => planAmount(a.price) - planAmount(b.price));
});
// Lead with the lowest-priced option: the smallest step from the free tier.
const recommendedId = computed(() => offers.value[0]?.product_id || '');

const title = computed(() => request.value?.trigger === 'download' ? t('paywall.downloadTitle') : t('paywall.creditsTitle'));
const description = computed(() => {
  if (request.value?.trigger === 'download') return t('paywall.downloadDescription');
  return hasSubscription.value ? t('paywall.memberCreditsDescription') : t('paywall.creditsDescription');
});

const offer = (plan: Plan) => planOffer(plan, plans.value, introEligible.value);
const songsFor = (points: number) => songsForCredits(points, musicCost.value);
// Allowance follows the plan's own billing period; annual prices are shown per month with the yearly total below.
const creditsLabel = (plan: Plan) => `${Number(plan.points).toLocaleString(locale.value)} ${t('billing.creditsPer_' + (['week', 'month', 'year'].includes(plan.tag) ? plan.tag : 'month'))}`;
const formatPrice = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
const baseParams = () => ({
  trigger: request.value?.trigger || '',
  surface: request.value?.surface || '',
  source: source.value,
  balance: totalPoints.value,
  required_points: request.value?.requiredPoints || 0,
  vip_level: userAccount.value?.vip_level ?? 0,
  page_path: route.path,
});

async function load() {
  const current = ++loadRequest;
  loading.value = true; loadError.value = false; error.value = '';
  const [catalog, account] = await Promise.allSettled([
    musicService.getSubscriptionConfig(),
    isAuthenticated.value ? fetchAndStoreToken().then(ok => ok ? billingService.config() : null) : Promise.resolve(null),
  ]);
  if (current !== loadRequest) return;
  if (catalog.status === 'fulfilled' && catalog.value?.code === 200 && Array.isArray(catalog.value.data?.list)) {
    plans.value = catalog.value.data.list;
    musicCost.value = Number(catalog.value.data.creation_pricing?.music?.time_all) || 0;
  } else {
    loadError.value = true;
  }
  config.value = account.status === 'fulfilled' ? account.value : null;
  loading.value = false;
  if (!loadError.value) trackEvent('paywall_plans_loaded', { ...baseParams(), offers: offers.value.length, has_subscription: hasSubscription.value });
}

async function choose(plan: Plan) {
  if (choosing.value) return;
  const params = { ...baseParams(), product_id: plan.product_id, plan_name: plan.name, price: offer(plan).display, recommended: plan.product_id === recommendedId.value };
  trackEvent('paywall_cta_click', params);
  if (!isAuthenticated.value) { await handleSignIn(); return; }
  choosing.value = plan.product_id; error.value = '';
  try {
    if (!await fetchAndStoreToken()) throw new Error('Authentication unavailable');
    if (hasSubscription.value) {
      const url = await billingService.upgrade(plan.product_id);
      rememberCheckoutSource(source.value, plan.product_id);
      trackEvent('subscription_upgrade_start', { ...params, current_product_id: config.value?.current_product_id });
      window.location.assign(url);
      return;
    }
    const path = await billingService.checkoutDestination(plan.product_id);
    if (path === '/billing') { closePaywall(); await router.push('/billing'); return; }
    rememberCheckoutSource(source.value, plan.product_id);
    trackEvent('checkout_start', params);
    if (isStripeHostedUrl(path)) { trackEvent('checkout_redirect', { provider: 'stripe', product_id: plan.product_id, source: source.value }); window.location.assign(path); }
    else { closePaywall(); await router.push(path); }
  } catch (failure: any) {
    error.value = failure?.response?.status === 409 ? 'billing.upgradeNotAvailable' : 'billing.unavailable';
    trackEvent('paywall_checkout_error', { ...params, status: failure?.response?.status || 0 });
  } finally { choosing.value = ''; }
}

function dismiss() {
  if (request.value) trackEvent('paywall_dismiss', baseParams());
  closePaywall();
}
function onCompare() {
  trackEvent('paywall_compare_plans', baseParams());
  closePaywall();
}

const { dialog, closeButton, handleKeydown } = useDialogFocus(visible, dismiss);

watch(request, (next, previous) => {
  if (!next) { ++loadRequest; choosing.value = ''; return; }
  if (previous && previous.trigger === next.trigger && previous.surface === next.surface) return;
  trackEvent('paywall_view', baseParams());
  void load();
});
// A route change means the user left the blocked action; do not carry the dialog along.
watch(() => route.path, () => { if (request.value) closePaywall(); });
</script>

<style scoped>
.plan-option {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px 14px; width: 100%;
  padding: 14px 16px; border-radius: 12px; border: 1px solid #374151; background: #111827;
  text-align: left; transition: border-color .15s, background-color .15s;
}
.plan-option:hover:not(:disabled) { border-color: #60a5fa; background: #172033; }
.plan-option.recommended { border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6 inset; }
.plan-option:disabled { opacity: .6; cursor: wait; }
.plan-option:focus-visible { outline: 2px solid #93c5fd; outline-offset: 3px; }
.plan-cta {
  flex-basis: 100%; display: flex; justify-content: center; align-items: center; min-height: 40px;
  border-radius: 8px; font-size: 13px; font-weight: 600; color: #fff; background: #1f2937;
}
.plan-option.recommended .plan-cta { background: linear-gradient(110deg, #2563eb, #4f46e5); }
</style>
